import React, { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';
import { Button } from '../ui/Button';
import { classNames } from '../../utils/classNames';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  placeholder?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  isLoading,
  placeholder = "Ask a new question or refine your last one...",
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !isLoading) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Set initial height to one line
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '36px';
      // Also set button height to match
      const button = textareaRef.current.parentElement?.nextElementSibling as HTMLElement;
      if (button) {
        button.style.height = '36px';
      }
    }
  }, []);

  // Handle input and resize immediately
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    
    // Immediately resize the textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    
    // If empty, force to exact initial height
    if (e.target.value === '') {
      textarea.style.height = '36px';
    } else {
      const scrollHeight = textarea.scrollHeight;
      const minHeight = 36;
      textarea.style.height = `${Math.max(scrollHeight, minHeight)}px`;
    }
    
    // Update button height to match
    const button = textarea.parentElement?.nextElementSibling as HTMLElement;
    if (button) {
      button.style.height = textarea.style.height;
    }
  };

  return (
    <div className="border-t theme-border-chat-separator bg-transparent p-1.5 md:p-2 lg:p-3 mt-auto">
      <form onSubmit={handleSubmit} className="flex gap-1.5 md:gap-2 lg:gap-3 items-end">
        <div className="flex-1 flex items-end">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            disabled={isLoading}
            className={classNames(
              'w-full resize-none rounded-xl border border-slate-600 px-2 py-1.5',
              'theme-bg-input-field backdrop-blur-sm theme-text-input',
              'focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
              'disabled:bg-slate-200/50 disabled:text-slate-500',
              'placeholder:theme-placeholder-input',
              'min-h-[36px]'
            )}
            style={{ maxHeight: '120px' }}
          />
        </div>
        
        <button
          type="submit"
          disabled={!message.trim() || isLoading}
          className={classNames(
            'px-2 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700',
            'border border-slate-600 shrink-0 rounded-xl font-medium transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'inline-flex items-center justify-center',
            'min-h-[36px]'
          )}
        >
          {!isLoading && <Send className="w-4 h-4" />}
          {isLoading && (
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
};